import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { fetchData } from '../../helpers/apiUtils';
import ImageUploadField from '../../components/ImageUploadField';
import { CAuthor } from './types';

/**
 * Edit a canonical broker-author. This is the E-E-A-T enrichment surface: the feed only
 * carries name (+ sometimes a photo), so bio / job title / expertise / sameAs are entered here
 * and then flow out in every portal's emitted feed.
 */
const AuthorEditDialog: React.FC<{
  authorId: number | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ authorId, onClose, onSaved }) => {
  const [author, setAuthor] = useState<CAuthor | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (authorId == null) {
      setAuthor(null);
      return;
    }
    (async () => {
      const res = await fetchData<CAuthor>(`/api/broker-content/authors/${authorId}`);
      if (!cancelled) {
        if (res.success && res.data) setAuthor(res.data);
        else setError(res.error || 'Autor konnte nicht geladen werden');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorId]);

  const setField = (key: keyof CAuthor, value: unknown) =>
    setAuthor((prev) => (prev ? { ...prev, [key]: value } : prev));

  const save = async () => {
    if (!author) return;
    setSaving(true);
    setError(null);
    const res = await fetchData(`/api/broker-content/authors/${author.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        display_name: author.display_name,
        job_title: author.job_title,
        expertise: author.expertise,
        bio: author.bio,
        photo_url: author.photo_url,
        status: author.status,
        sameas: (author.sameas || []).filter((s) => s && s.trim()),
      }),
    });
    setSaving(false);
    if (res.success) onSaved();
    else setError(res.error || 'Speichern fehlgeschlagen');
  };

  return (
    <Dialog open={authorId != null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Autor bearbeiten</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        {author && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Slug (fix, dashboard-eigen)"
              value={author.slug}
              disabled
              size="small"
            />
            <TextField
              label="Anzeigename"
              value={author.display_name || ''}
              onChange={(e) => setField('display_name', e.target.value)}
              size="small"
            />
            <TextField
              label="Jobbezeichnung"
              value={author.job_title || ''}
              onChange={(e) => setField('job_title', e.target.value)}
              size="small"
            />
            <TextField
              label="Schwerpunkte"
              value={author.expertise || ''}
              onChange={(e) => setField('expertise', e.target.value)}
              size="small"
            />
            <ImageUploadField
              label="Autoren-Foto"
              uploadUrl="/api/broker-content/upload-image?kind=authors"
              value={author.photo_url}
              onChange={(url) => setField('photo_url', url)}
            />
            <TextField
              label="Biografie"
              value={author.bio || ''}
              onChange={(e) => setField('bio', e.target.value)}
              multiline
              minRows={3}
              size="small"
            />
            <TextField
              label="sameAs (E-E-A-T, eine URL pro Zeile)"
              value={(author.sameas || []).join('\n')}
              onChange={(e) => setField('sameas', e.target.value.split('\n'))}
              multiline
              minRows={2}
              size="small"
              helperText="LinkedIn / Xing / X etc. — für das Person-Schema der Portale."
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={save} disabled={saving || !author}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthorEditDialog;
